/**
 * La marque d'une école, telle qu'elle habille l'espace entreprise.
 *
 * Ce que la marque change : son ILLUSTRATION, son LOGO, sa COULEUR D'ACTION, et
 * ses TEXTES. Rien d'autre.
 *
 * Elle a beaucoup maigri, et c'est le but. La palette complète (encre, atténué,
 * surfaces, traits) et la police venaient de moi ; le référentiel du proto fixe
 * ces valeurs, et les laisser surchargeables cassait son échelle typographique et
 * ses contrastes. L'identité passe par l'illustration et le logo, qui ne peuvent
 * rien casser.
 */

/**
 * Le format d'une couleur de marque : « 244 75% 59% », HSL sans la fonction.
 *
 * Il vit ici parce que les DEUX côtés le vérifient : le serveur refuse une valeur
 * mal formée à l'écriture, le front l'ignore à l'affichage. Deux expressions
 * régulières pour un seul format finiraient par ne plus dire la même chose.
 */
export const HSL_TRIPLET = /^\d{1,3}(\.\d+)?\s+\d{1,3}(\.\d+)?%\s+\d{1,3}(\.\d+)?%$/;

export type Brand = {
	slug: string;
	schoolName: string;
	campusName?: string | null;
	logoUrl?: string | null;
	/** L'image du panneau de droite. C'est elle qui porte l'identité, pas la couleur. */
	illustrationUrl?: string | null;
	baseline?: string | null;

	/**
	 * La couleur d'action, en HSL sans la fonction : "244 75% 59%".
	 * Elle surcharge `primary` — dans ce référentiel, `accent` est l'esthétique et
	 * le catégoriel, jamais un bouton.
	 */
	accent: string;
	accentContrast?: string;

	/**
	 * La couleur de l'APLAT de navigation, si l'école en veut une autre que sa
	 * couleur d'action. Même format HSL, et une seule valeur : les cinq autres
	 * valeurs de la barre s'en dérivent, y compris la couleur du texte, qui se
	 * calcule par luminance et ne se saisit jamais.
	 *
	 * Deux rôles distincts, et c'est la raison d'être du champ : une couleur
	 * d'action est faite pour RESSORTIR sur du blanc, un aplat pleine hauteur est
	 * fait pour se TENIR en arrière-plan. Absente, l'aplat reprend l'action.
	 */
	navBase?: string | null;

	/** Les textes que l'école écrit elle-même. */
	pageTitle?: string | null;
	pageIntro?: string | null;
	confirmationMessage?: string | null;

	/** L'interlocuteur dédié, pas « l'école » en général. */
	contactName?: string | null;
	contactEmail?: string | null;
	contactPhone?: string | null;

	/** Quels champs l'école demande, et lesquels sont obligatoires. */
	formFields: FormFieldConfig[];
};

export type FormFieldConfig = {
	key: FormFieldKey;
	enabled: boolean;
	required: boolean;
	/** Libellé réécrit par l'école. Absent, on prend celui du catalogue. */
	label?: string;
};

/**
 * Le catalogue des champs. L'école CHOISIT dedans, elle n'invente pas :
 * un champ libre côté école deviendrait une colonne que personne n'exploite,
 * et un formulaire que personne ne remplit.
 */
export const FIELD_CATALOGUE = {
	job: { label: "Intitulé du poste", type: "text", core: true },
	description: { label: "Missions et profil recherché", type: "textarea", core: true },
	location: { label: "Ville", type: "text", core: false },
	contract: { label: "Type de contrat", type: "select", core: false },
	telework: { label: "Télétravail", type: "select", core: false },
	qualification: { label: "Niveau de formation visé", type: "select", core: false },
	startsOn: { label: "Date de démarrage souhaitée", type: "date", core: false },
	headcount: { label: "Nombre de postes", type: "number", core: false },
	salary: { label: "Rémunération envisagée", type: "text", core: false },
	department: { label: "Service ou équipe", type: "text", core: false },
	offerLink: { label: "Lien vers votre annonce", type: "url", core: false },
} as const;

export type FormFieldKey = keyof typeof FIELD_CATALOGUE;

/**
 * Les clés du catalogue, sous une forme que zod garde TYPÉE.
 *
 * `Object.keys()` rend des `string[]` : une validation bâtie dessus accepte bien
 * les onze clés à l'exécution, mais le type qui en sort est `string`, et il ne
 * s'écrit plus dans un `FormFieldConfig`. Le cast vit donc ici, une fois, à côté
 * du catalogue qu'il décrit.
 */
export const FORM_FIELD_KEYS = Object.keys(FIELD_CATALOGUE) as [FormFieldKey, ...FormFieldKey[]];

export const CONTRACT_OPTIONS = ["Alternance", "Stage", "CDI", "CDD", "Freelance"] as const;
export const TELEWORK_OPTIONS = ["Sur site", "Hybride", "Télétravail complet"] as const;
export const QUALIFICATION_OPTIONS = ["Bac+2", "Bac+3", "Bac+5", "Indifférent"] as const;

/** La configuration par défaut : le minimum qu'ACE demande, « un formulaire allégé ». */
export const DEFAULT_FORM_FIELDS: FormFieldConfig[] = [
	{ key: "job", enabled: true, required: true },
	{ key: "description", enabled: true, required: true },
	{ key: "location", enabled: true, required: true },
	{ key: "contract", enabled: true, required: true },
	{ key: "startsOn", enabled: true, required: false },
	{ key: "telework", enabled: false, required: false },
	{ key: "qualification", enabled: false, required: false },
	{ key: "headcount", enabled: false, required: false },
	{ key: "salary", enabled: false, required: false },
	{ key: "department", enabled: false, required: false },
	{ key: "offerLink", enabled: false, required: false },
];

/**
 * Les illustrations de repli, reprises telles quelles de main-app.
 *
 * main-app en tire une AU HASARD à chaque affichage. Sur une page de connexion ça
 * passe ; ici une entreprise revient déposer une seconde demande, et voir une autre
 * image lui ferait croire qu'elle s'est trompée de page. On tire donc toujours la
 * même pour une école donnée, à partir de son slug.
 */
export const FALLBACK_ILLUSTRATIONS = [
	"/illustrations/login_blue.svg",
	"/illustrations/login_green.svg",
	"/illustrations/login_orange.svg",
	"/illustrations/login_pink.svg",
	"/illustrations/login_yellow.svg",
] as const;

export function illustrationFor(brand: { slug: string; illustrationUrl?: string | null }): string {
	if (brand.illustrationUrl) return brand.illustrationUrl;
	let hash = 0;
	for (const char of brand.slug) hash = (hash * 31 + char.charCodeAt(0)) % 100_000;
	return FALLBACK_ILLUSTRATIONS[hash % FALLBACK_ILLUSTRATIONS.length] as string;
}

/**
 * La couleur d'action du référentiel : `--mg-action-primary` du thème V3.2 du
 * proto, soit #455f9b.
 *
 * ⚠️ C'était `244 75% 59%`, un violet que j'avais choisi et que les 62 écoles
 * importées portaient toutes — l'import ne reprend pas la couleur de main-app,
 * où 5 écoles sur 62 en ont une et où les cinq valent #000000. Tout l'espace
 * s'affichait donc dans une couleur qui n'existe nulle part au référentiel.
 *
 * Le triplet est celui que rend `hexToHslTriplet("#455f9b")`, à la décimale près :
 * l'aller-retour hex ↔ HSL doit revenir EXACTEMENT sur #455f9b, et
 * `tests/brand.test.ts` le vérifie.
 */
export const ACTION_V32 = "221.9 38.4% 43.9%";

/**
 * L'aplat de navigation du référentiel : `--mg-nav-bg`, soit #263171.
 *
 * Il est plus SOMBRE que la couleur d'action, et c'est le point : une couleur
 * d'action est faite pour ressortir sur du blanc, un aplat pleine hauteur est fait
 * pour se tenir en arrière-plan.
 */
export const NAV_V32 = "231.2 49.7% 29.6%";

export const DEFAULT_BRAND: Brand = {
	slug: "demo",
	schoolName: "Mentor Goal",
	accent: ACTION_V32,
	navBase: NAV_V32,
	formFields: DEFAULT_FORM_FIELDS,
};
